<template>
  <RichTextRenderer
    :document="text"
    :node-renderers="nodesRenderer"
    :mark-renderers="markRenderers"
  />
</template>

<script setup lang="ts">
import RichTextRenderer, {
  type MarksRenderer,
  type NodesRenderer
} from 'contentful-rich-text-vue-renderer';
import type { Block, Document } from '@contentful/rich-text-types';
import { BLOCKS, INLINES, MARKS, type Text } from '@contentful/rich-text-types';
import { TableBlock } from '@hypercodestudio/basler-components';
// Vue runtime functions like h() are auto-imported in Nuxt
import MappedComponent from '~/components/global/MappedComponent.vue';
import Link from '~/components/global/contentful/Link.vue';
import isChildOf from '~/utils/richtext/isChildOf';
import { isEmpty } from '~/utils/isEmpty';

interface Props {
  richText: Document;
  hasLightVerticalHeader?: boolean;
  cols?: number;
  colsCentered?: boolean;
  contentCentered?: boolean;
}

const props = defineProps<Props>();

const convertNewlinesToBreaks = (text: string) => {
  const parts = text.split('\n');
  return parts.flatMap((part, index) => {
    return index < parts.length - 1 ? [part, h('br')] : [part];
  });
};

/* DBP-2495 Workaround to remove empty paragraph*/
const text = computed(() => {
  const content = props.richText;

  const lastContent = content.content[content.content.length - 1];
  if (lastContent && lastContent.content && lastContent.content.length === 1) {
    const lastNode = lastContent.content[0];

    // Type guard to check if the node is a Text node
    if (
      lastNode.nodeType === 'text' &&
      'value' in lastNode &&
      isEmpty((lastNode as Text).value)
    ) {
      delete content.content[content.content.length - 1];
    }
  }

  return content;
});
const markRenderers: MarksRenderer = {
  [MARKS.STRIKETHROUGH]: (text: Text | string[], key, _next) => {
    return h(
      'span',
      { style: 'text-decoration: line-through;', key },
      text as unknown as string
    );
  }
};

const nodesRenderer: NodesRenderer = {
  [BLOCKS.EMBEDDED_ENTRY]: (node, key) =>
    h(MappedComponent, {
      key,
      item: node.data.target
    }),
  [INLINES.EMBEDDED_ENTRY]: (node, key) =>
    h(MappedComponent, {
      key,
      item: node.data.target
    }),
  [INLINES.ENTRY_HYPERLINK]: (node, key, next) => {
    return h(
      Link,
      {
        key,
        item: node.data?.target,
        useSlot: true
      },
      next?.(node.content as Block[], key, next)
    );
  },
  [INLINES.ASSET_HYPERLINK]: (node, key, next) => {
    return h(
      'a',
      {
        key,
        href: node.data.target.fields?.file?.url,
        download: true,
        target: '_blank'
      },
      next?.(node.content as Block[], key, next)
    );
  },
  [BLOCKS.TABLE_HEADER_CELL]: (node, key, next) => {
    return h(
      'th',
      { key, scope: 'col' },
      next?.(node.content as Block[], key, next)
    );
  },
  [BLOCKS.OL_LIST]: (node, key, next) => {
    return h(
      'ol',
      {
        class: `level-${
          ((key?.replace('RichText--', '').split('-').length ?? 1) + 1) / 2
        }`
      },
      next?.(node.content as Block[], key, next)
    );
  },
  [BLOCKS.TABLE]: (node, key, next) => {
    const tableRows = node.content as Block[];

    if (!tableRows || tableRows.length === 0) {
      return;
    }

    const firstTableRow = tableRows[0];
    const renderTableHead = () =>
      h(
        'thead',
        {},
        h('tr', {}, next?.(firstTableRow.content as Block[], key, next))
      );

    const tableRowCollection = tableRows
      .slice(1)
      .reduce<(() => any)[]>((tableRowCollection, currentTableRow) => {
        const currentRowCells = currentTableRow.content as Block[];

        if (!currentRowCells || currentRowCells.length === 0) {
          return tableRowCollection;
        }

        const firstRowCell = currentRowCells[0];
        const thFirstRowCell = () =>
          h(
            'th',
            { scope: 'row' },
            next?.(firstRowCell.content as Block[], key, next)
          );

        const restRowCells = currentRowCells.slice(1);
        const tdRestRowCells: (() => any)[] = restRowCells.reduce<
          (() => any)[]
        >(
          (tdRestRowCells, currentElement) => [
            ...tdRestRowCells,
            () =>
              h('td', {}, next?.(currentElement.content as Block[], key, next))
          ],
          []
        );

        return [
          ...tableRowCollection,
          () => h('tr', {}, [thFirstRowCell(), tdRestRowCells.map((x) => x())])
        ];
      }, []);

    const renderTableBody = () =>
      h(
        'tbody',
        {},
        tableRowCollection.map((x) => x())
      );

    return h(
      TableBlock,
      {
        hasLightVerticalHeader: props.hasLightVerticalHeader ?? false,
        cols: props.cols ?? 12,
        colsCentered: props.colsCentered ?? true,
        contentCentered: props.contentCentered ?? false
      },
      () => h('table', {}, [renderTableHead(), renderTableBody()])
    );
  },
  text: (node, key, markRenderer) => {
    if (!node.marks.length) {
      // avoid hydration mismatches
      // XXX: why are "\r" included in response? not normalized in
      //  contentful? "\r" is not rendered on client side.
      const cleanedValue = node.value?.replaceAll('\r', '');

      return cleanedValue === '' &&
        !isChildOf('unordered-list', key ?? '', text.value)
        ? h('br')
        : cleanedValue.split('\n').length > 1
        ? h('span', { key }, convertNewlinesToBreaks(node.value))
        : cleanedValue;
    }

    const marksReversed = [...node.marks].reverse();

    return marksReversed.reduce(
      // XXX: proper typing
      (aggregate: any, mark, i) => {
        if (
          mark.type in markRenderer &&
          typeof markRenderer[mark.type] === 'function'
        ) {
          return markRenderer[mark.type]([aggregate], `${key}-${i}`, h);
        }
        if (node.value) {
          return aggregate;
        }
      },
      node.value
    );
  }
};
</script>

<style>
.rte-text ol {
  list-style: none;
  padding-left: 0;
}

.rte-text ol.level-1 > li {
  counter-increment: level1;
  counter-reset: level2;
  margin-left: 0;
  padding-left: 1.5em;
  position: relative;
}

.rte-text ol.level-1 > li::before {
  content: counter(level1) '. ';
  position: absolute;
  left: 0;
}

.rte-text ol.level-2 {
  counter-reset: level2;
  padding-left: 1.5em;
}

.rte-text ol.level-2 > li {
  counter-increment: level2;
  counter-reset: level3;
  position: relative;
  padding-left: 2em;
}

.rte-text ol.level-2 > li::before {
  content: counter(level1) '.' counter(level2) ' ';
  position: absolute;
  left: 0;
}

.rte-text ol.level-3 > li {
  counter-increment: level3;
  position: relative;
  padding-left: 3em;
}

.rte-text ol.level-3 > li::before {
  content: counter(level1) '.' counter(level2) '.' counter(level3) ' ';
  position: absolute;
  left: 0;
}

.rte-text ol.level-4 > li {
  counter-increment: level4;
  counter-reset: level5;
  position: relative;
  padding-left: 4em;
}

.rte-text ol.level-4 > li::before {
  content: counter(level1) '.' counter(level2) '.' counter(level3) '.'
    counter(level4) ' ';
  position: absolute;
  left: 0;
}

.rte-text ol.level-5 > li {
  counter-increment: level5;
  counter-reset: level6;
  position: relative;
  padding-left: 5em;
}

.rte-text ol.level-5 > li::before {
  content: counter(level1) '.' counter(level2) '.' counter(level3) '.'
    counter(level4) '.' counter(level5) ' ';
  position: absolute;
  left: 0;
}

.rte-text ol.level-6 > li {
  counter-increment: level6;
  counter-reset: level7;
  position: relative;
  padding-left: 6em;
}

.rte-text ol.level-6 > li::before {
  content: counter(level1) '.' counter(level2) '.' counter(level3) '.'
    counter(level4) '.' counter(level5) '.' counter(level6) ' ';
  position: absolute;
  left: 0;
}

.rte-text ol.level-7 > li {
  counter-increment: level7;
  counter-reset: level8;
  position: relative;
  padding-left: 7em;
}

.rte-text ol.level-7 > li::before {
  content: counter(level1) '.' counter(level2) '.' counter(level3) '.'
    counter(level4) '.' counter(level5) '.' counter(level6) '.' counter(level7)
    ' ';
  position: absolute;
  left: 0;
}

.rte-text ol.level-8 > li {
  counter-increment: level8;
  counter-reset: level9;
  position: relative;
  padding-left: 8em;
}

.rte-text ol.level-8 > li::before {
  content: counter(level1) '.' counter(level2) '.' counter(level3) '.'
    counter(level4) '.' counter(level5) '.' counter(level6) '.' counter(level7)
    '.' counter(level8) ' ';
  position: absolute;
  left: 0;
}

.rte-text ol.level-9 > li {
  counter-increment: level9;
  counter-reset: level10;
  position: relative;
  padding-left: 9em;
}

.rte-text ol.level-9 > li::before {
  content: counter(level1) '.' counter(level2) '.' counter(level3) '.'
    counter(level4) '.' counter(level5) '.' counter(level6) '.' counter(level7)
    '.' counter(level8) '.' counter(level9) ' ';
  position: absolute;
  left: 0;
}

.rte-text ol.level-10 > li {
  counter-increment: level10;
  position: relative;
  padding-left: 10em;
}

.rte-text ol.level-10 > li::before {
  content: counter(level1) '.' counter(level2) '.' counter(level3) '.'
    counter(level4) '.' counter(level5) '.' counter(level6) '.' counter(level7)
    '.' counter(level8) '.' counter(level9) '.' counter(level10) ' ';
  position: absolute;
  left: 0;
}
</style>
